import { useState } from 'react';
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  alpha,
  TextField,
  Alert,
  Box,
} from '@mui/material';
import DnsIcon from '@mui/icons-material/Dns';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';

const RedirectUriForm = ({
  app,
  deployment,
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
}) => {
  const [redirectUri, setRedirectUri] = useState(deployment?.redirectUri || '');
  const [error, setError] = useState('');

  const isValidDomain = (uri) => {
    try {
      const url = new URL(uri);
      return url.protocol === 'https:' && url.hostname.includes('.');
    } catch {
      return false;
    }
  };

  const handleUriChange = (event) => {
    const value = event.target.value;
    setRedirectUri(value);

    if (!value) {
      setError('Bitte geben Sie eine URI ein');
    } else if (!isValidDomain(value)) {
      setError(
        'Bitte geben Sie eine gültige HTTPS-URI ein (z.B. https://app.example.com)',
      );
    } else {
      setError('');
    }
  };

  const handleSave = () => {
    if (!error && redirectUri) {
      next({ redirectUri });
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<DnsIcon color="primary" />}
        title={
          <Typography variant="h6">Deployment-URI Konfiguration</Typography>
        }
        subheader={`Für ${app?.title || deployment?.title}`}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Geben Sie die URI ein, unter der die Anwendung erreichbar sein soll.
          Sie benötigen Zugriff auf die DNS-Einstellungen dieser Domain.
        </Typography>
        {deployment ? (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Aktuelle URI: <strong>{deployment.redirectUri}</strong>
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Wenn Sie die URI ändern, müssen Sie ihre DNS-Einträge erneut
              setzten, um zu verifizieren, dass diese Domain ihnen gehört.
            </Alert>
          </>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            Die URI muss über HTTPS erreichbar sein und Sie müssen die
            Berechtigung haben, DNS-Einträge für diese Domain zu konfigurieren.
            Nach der Buchung erhalten Sie eine E-Mail mit einer Anleitung zur
            Einrichtung der App.
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Deployment URI"
            value={redirectUri}
            onChange={handleUriChange}
            error={!!error}
            helperText={error || ''}
            placeholder="https://app.example.com"
          />
        </Box>
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={next && handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          disabled={!!error || !redirectUri}
        />
      </CardActions>
    </Card>
  );
};

export default RedirectUriForm;
